import { bookingTypeOptions } from 'Models/Enums';
import { useEffect } from 'react';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'Modules/Ui';
import NavigationWrapper from '../Components/Navigation/NavigationWrapper';
import { removeFromWaitlist, RemoveFromWaitlistDto } from 'Services/Api/_HumanWritten/WaitlistService';
import { LottieSpinner } from '../Components/_HumanWritten/Lottie/LottieSpinner';
import { LottieWaitlistRemoved } from '../Components/_HumanWritten/Lottie/LottieWaitlistRemoved';
import moment from 'moment/moment';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import { REMOVE_FROM_WAITLIST_PAGE_TITLE } from 'ConstantPageNames';
import { ROUTES } from 'Constants';

/** Milliseconds for sorting, with a missing or unparseable departure pushed to the end of the list. */
const departureSortKey = (departure?: Date) => {
	const time = departure === undefined || departure === null ? NaN : new Date(departure).getTime();
	return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
};

export default function RemoveFromWaitlistPage() {
	return <RemoveFromWaitlist />;
}

export interface RemoveFromWaitlistProps {}

export function RemoveFromWaitlist() {
	const [showRemoveFromAllWaitlists, setShowRemoveFromAllWaitlists] = React.useState(true);
	const [waitlistItems, setWaitlistItems] = React.useState<RemoveFromWaitlistDto[]>([]);
	const [isLoading, setIsLoading] = React.useState(true);
	const [isError, setIsError] = React.useState(false);

	useEffect(() => {
		document.title = REMOVE_FROM_WAITLIST_PAGE_TITLE;
	}, []);

	// The id arrives in the query string (/remove-from-waitlist?waitlistItemId=...) because that is the
	// link the waitlist emails build. It is not a route segment, so useParams never sees it.
	const [searchParams] = useSearchParams();
	const waitlistId = searchParams.get('waitlistItemId') ?? '';

	const fetchWaitlistData = async (removeAll = false) => {
		setIsLoading(true);
		setIsError(false);

		// Without an id there is nothing to remove, and the request would only come back as a 400.
		if (waitlistId === '') {
			setIsError(true);
			setIsLoading(false);
			return;
		}

		try {
			const result = await removeFromWaitlist(waitlistId, removeAll);
			setWaitlistItems(result);
			setShowRemoveFromAllWaitlists(!removeAll); // hide button after full removal
		} catch (e) {
			// Logged as well as shown: the page only ever says "there was an error", so without this
			// there is nothing anywhere saying whether the link was bad, the item was already gone, or
			// the request failed outright.
			console.error('Failed to remove from waitlist', e);
			setIsError(true);
		}
		setIsLoading(false);
	};

	React.useEffect(() => {
		fetchWaitlistData(false);
	}, []);

	// One row per trip, soonest first, rather than the response's own shape.
	//
	// The endpoint groups its trips under the route they belong to, so the dates only ever climbed
	// WITHIN a route - a route whose first trip was the 9th could list the 11th above a second route
	// starting on the 10th, and the page read as though it were unordered. Chronology is what the
	// customer is scanning for here, so the route moves onto the row instead of heading a block of
	// them.
	const removedTrips = React.useMemo(
		() =>
			waitlistItems
				.flatMap(item =>
					(item.moreInformation ?? []).map(trip => ({
						route: `${item.departureName} to ${item.destinationName}`,
						departureDateTime: trip.departureDateTime,
						bookingType: trip.bookingType,
					})),
				)
				// A trip with no departure sorts last rather than to the top, which is where an
				// invalid date would otherwise land it.
				.sort((a, b) => departureSortKey(a.departureDateTime) - departureSortKey(b.departureDateTime)),
		[waitlistItems],
	);

	if (isLoading) {
		return <LottieSpinner />;
	}

	const pageTitle = isError ? 'Could not be removed from waitlist' : 'Removed from waitlist';

	// Plain strings rather than elements: the body used to be a <p> rendered inside another <p>,
	// which the browser closes early — so the paragraph's own styling never applied to it.
	const pageBody = isError
		? 'There was an error removing you from the waitlist. Please try again or contact staff if the issue persists.'
		: 'You have been removed from the following waitlists:';

	return (
		<div className="body-content remove-from-waitlist-page">
			<NavigationWrapper className="booking-success__nav" />
			<div className="waitlist-removed-content">
				<div className="waitlist-removed-card">
					{!isError && <LottieWaitlistRemoved />}
					<h1 className="waitlist-removed__headline">{pageTitle}</h1>
					<p className="waitlist-removed__body">{pageBody}</p>

					{!isError && removedTrips.length > 0 && (
						<ul className="waitlist-removed__list">
							{removedTrips.map(trip => (
								<li
									className="waitlist-removed__list-item"
									key={`${trip.route}_${trip.departureDateTime}_${trip.bookingType}`}
								>
									<span className="waitlist-removed__trip-when">
										{`${moment(trip.departureDateTime).format('dddd MMM Do')}, ${formatStandardTime(trip.departureDateTime)}`}
									</span>
									<span className="waitlist-removed__trip-route">
										{trip.route} ({bookingTypeOptions[trip.bookingType]})
									</span>
								</li>
							))}
						</ul>
					)}

					<div className="waitlist-removed__actions">
						{/* asLink rather than navigate(): it is a plain destination, so it should be a real
						    link that can be opened in a new tab or copied. */}
						<Button className="go-to-ferry-schedule-link" asLink to={ROUTES.FERRY_SCHEDULE}>
							Go to ferry schedule
						</Button>
						{!isError && showRemoveFromAllWaitlists && (
							<Button
								className="remove-from-all-waitlists-button"
								variant="text"
								onClick={() => fetchWaitlistData(true)}
							>
								Remove me from all waitlists
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
